import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Search } from "lucide-react";
import { toast } from "sonner";

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [duplicatesOnly, setDuplicatesOnly] = useState(false);
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(false);
  const limit = 100;

  const load = async (resetSkip = false) => {
    setLoading(true);
    try {
      const params = { limit, skip: resetSkip ? 0 : skip };
      if (q) params.q = q;
      if (start) params.start_date = start;
      if (end) params.end_date = end;
      if (duplicatesOnly) params.duplicates_only = true;
      const { data } = await api.get("/leads", { params });
      setLeads(data.leads || []);
      setTotal(data.total || 0);
      if (resetSkip) setSkip(0);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [skip]);
  useEffect(() => { load(true); /* eslint-disable-next-line */ }, [duplicatesOnly]);

  return (
    <div className="p-6 md:p-10 max-w-[1600px]" data-testid="leads-page">
      <div className="mb-8 fade-up">
        <div className="text-[11px] font-semibold tracking-[0.22em] uppercase text-gray-500">Database</div>
        <h1 className="font-display text-4xl font-bold tracking-tight text-gray-950 mt-2">Leads</h1>
        <p className="text-gray-600 mt-2">Search, filter and review all ingested leads.</p>
      </div>

      <Card className="rounded-sm border border-gray-200 shadow-none p-5 bg-white mb-5">
        <div className="grid md:grid-cols-6 gap-3">
          <div className="md:col-span-2">
            <Label className="text-[10px] tracking-[0.18em] uppercase text-gray-600">Search</Label>
            <div className="relative mt-1">
              <Search size={14} className="absolute left-3 top-3 text-gray-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="phone, email, name" className="pl-9 h-10 rounded-sm" data-testid="leads-search-input" />
            </div>
          </div>
          <div>
            <Label className="text-[10px] tracking-[0.18em] uppercase text-gray-600">From</Label>
            <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="mt-1 h-10 rounded-sm" data-testid="leads-start-date" />
          </div>
          <div>
            <Label className="text-[10px] tracking-[0.18em] uppercase text-gray-600">To</Label>
            <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="mt-1 h-10 rounded-sm" data-testid="leads-end-date" />
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-2 text-sm text-gray-700 h-10">
              <input type="checkbox" checked={duplicatesOnly} onChange={(e) => setDuplicatesOnly(e.target.checked)} data-testid="leads-dup-only" />
              Dup only
            </label>
          </div>
          <div className="flex items-end">
            <Button onClick={() => load(true)} className="w-full rounded-sm h-10 bg-[#002FA7] hover:bg-[#002480] text-white" data-testid="leads-apply">Apply</Button>
          </div>
        </div>
      </Card>

      <Card className="rounded-sm border border-gray-200 shadow-none bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="leads-table">
            <thead className="bg-gray-50 sticky top-0">
              <tr className="text-[10px] tracking-wider uppercase text-gray-500">
                <th className="text-left px-4 py-3">Phone</th>
                <th className="text-left px-4 py-3">Email</th>
                <th className="text-left px-4 py-3">Name</th>
                <th className="text-left px-4 py-3">City, State</th>
                <th className="text-left px-4 py-3">Zip</th>
                <th className="text-left px-4 py-3">Source Date</th>
                <th className="text-left px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-500">Loading…</td></tr>}
              {!loading && leads.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-12 text-center text-gray-500">No leads found.</td></tr>
              )}
              {!loading && leads.map((l) => (
                <tr key={l.id} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`lead-row-${l.id}`}>
                  <td className="px-4 py-2.5 font-mono">{l.phone}</td>
                  <td className="px-4 py-2.5 text-xs text-gray-700">{l.email}</td>
                  <td className="px-4 py-2.5">{l.first_name} {l.last_name}</td>
                  <td className="px-4 py-2.5 text-xs text-gray-700">{l.city}{l.city && l.state ? ", " : ""}{l.state}</td>
                  <td className="px-4 py-2.5 font-mono text-xs">{l.zip}</td>
                  <td className="px-4 py-2.5 font-mono text-xs">{l.source_date}</td>
                  <td className="px-4 py-2.5">
                    {l.is_duplicate ? (
                      <span className="inline-block text-[10px] tracking-wider uppercase font-semibold text-[#FF3B30] border border-[#FF3B30]/30 bg-[#FF3B30]/5 px-2 py-0.5 rounded-sm">Duplicate</span>
                    ) : (
                      <span className="inline-block text-[10px] tracking-wider uppercase font-semibold text-[#00C04B] border border-[#00C04B]/30 bg-[#00C04B]/5 px-2 py-0.5 rounded-sm">New</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 text-sm text-gray-600">
          <div data-testid="leads-pagination-info">
            {leads.length ? `${skip + 1}–${skip + leads.length}` : "0"} of {total.toLocaleString()}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="rounded-sm" disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - limit))} data-testid="leads-prev">Prev</Button>
            <Button variant="outline" size="sm" className="rounded-sm" disabled={skip + limit >= total} onClick={() => setSkip(skip + limit)} data-testid="leads-next">Next</Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
